// src/api/ordenes.js - Consulta y seguimiento seguro de órdenes de equipo en 2 pasos
import { apiFetch } from './index.js';
import { obtenerTasaVentaActual } from './tasaVenta.js';

/**
 * Normaliza un documento de identidad para comparaciones seguras
 * Remueve puntos, guiones, espacios y prefijos estándar (V, E, J, G)
 */
export const normalizarDocumento = (doc) => {
  if (!doc) return '';
  return doc.toString().replace(/[^a-zA-Z0-9]/g, '').toUpperCase().replace(/^[VEJG]/, '');
};

/**
 * Datos de demostración en caso de modo offline o pruebas
 */
const ORDEN_DEMO = {
  idRecepcionEquipo: 999,
  numeroOrden: 'ORD-2024-001',
  fechaIngreso: new Date().toISOString(),
  nombreEstado: 'En Reparación',
  idEstadoRecepcion: 3,
  nombreCliente: 'Carlos Alberto Mendoza',
  documentoCliente: 'V-12345678',
  telefonoCliente: '0414-1234567',
  emailCliente: 'carlos.mendoza@email.com',
  direccionCliente: 'Av. Bolívar, Res. Los Sauces, Apto 4-B',
  nombreTecnicoRecepcion: 'Jean Rodríguez',
  nombreTecnicoReparador: 'Técnico Especialista JHC',
  nombreTecnicoEntrega: 'Pendiente de entrega',
  descripcion: 'Laptop ASUS ZenBook 14 - Intel Core i7 16GB RAM',
  problema: 'No enciende, recalentamiento repentino y fallo de carga por puerto Tipo C',
  tipoDiagnostico: 'GRATIS',
  montoDiagnostico: 0,
  accesorios: 'Cargador original 65W, funda protectora azul',
  clavePatron: 'No posee clave',
  problemaEncontrado: 'Cortocircuito en el circuito de entrada de alimentación (MOSFET primario dañado) y pasta térmica cristalizada.',
  actividadesRealizadas: '1. Desensamble y limpieza profunda de placa madre.\n2. Reemplazo de MOSFET de entrada y condensadores de filtro.\n3. Mantenimiento térmico con cambio de pasta térmica de alta conductividad.\n4. Pruebas de encendido y test de estabilidad bajo carga de 2 horas satisfactorias.',
  precioTotalUsd: 45.0,
  recepcionEquipoDetalles: [
    {
      uuid: 'd1',
      nombre: 'Reparación a nivel de componentes (Circuito de carga)',
      tipo: 'servicio',
      cantidad: 1,
      garantia: 90,
      precio: 35.0,
      subtotalUsd: 35.0
    },
    {
      uuid: 'd2',
      nombre: 'Mantenimiento Preventivo y Cambio de Pasta Térmica Artic MX-4',
      tipo: 'servicio',
      cantidad: 1,
      garantia: 30,
      precio: 10.0,
      subtotalUsd: 10.0
    }
  ],
  repuestosManuales: [
    {
      uuid: 'r1',
      nombre: 'Componente MOSFET canal N 30V SMD',
      precio: 5.0
    }
  ]
};

/**
 * PASO 1: Busca si el número de orden de servicio existe
 * @param {string} numeroOrden
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const buscarOrdenPorNumero = async (numeroOrden) => {
  try {
    const ordenLimpia = (numeroOrden || '').trim();
    if (!ordenLimpia) {
      return { success: false, error: 'Por favor ingrese el número de orden de servicio.' };
    }

    // Demo rápido
    if (ordenLimpia.toUpperCase() === 'ORD-2024-001' || ordenLimpia === '1' || ordenLimpia === '001') {
      return {
        success: true,
        data: {
          idRecepcionEquipo: ORDEN_DEMO.idRecepcionEquipo,
          numeroOrden: ORDEN_DEMO.numeroOrden,
          documentoRegistrado: ORDEN_DEMO.documentoCliente,
          esDemo: true
        }
      };
    }

    let ordenEncontrada = null;

    // 1. Buscar en endpoint con filtro
    try {
      const searchRes = await apiFetch(
        `/RecepcionEquipo/search?numeroOrden=${encodeURIComponent(ordenLimpia)}`
      );
      const lista = Array.isArray(searchRes) ? searchRes : (searchRes?.data || []);
      ordenEncontrada = lista.find(
        (o) =>
          o.numeroOrden?.toString().toUpperCase() === ordenLimpia.toUpperCase() ||
          o.idRecepcionEquipo?.toString() === ordenLimpia
      );
    } catch (searchErr) {
      console.warn('Fallo búsqueda filtrada, intentando lista general...', searchErr);
    }

    // 2. Si no se encontró por búsqueda directa, buscar en lista general
    if (!ordenEncontrada) {
      try {
        const allRes = await apiFetch('/RecepcionEquipo');
        const lista = Array.isArray(allRes) ? allRes : (allRes?.data || []);
        ordenEncontrada = lista.find(
          (o) =>
            o.numeroOrden?.toString().toUpperCase() === ordenLimpia.toUpperCase() ||
            o.idRecepcionEquipo?.toString() === ordenLimpia
        );
      } catch (allErr) {
        console.warn('Fallo listado general:', allErr);
      }
    }

    if (!ordenEncontrada) {
      return {
        success: false,
        error: `No encontramos ninguna orden con el número "${ordenLimpia}". Verifique su comprobante e intente de nuevo.`
      };
    }

    // Obtener documento asociado para la posterior validación
    let docRegistrado = ordenEncontrada.documentoCliente;
    if (!docRegistrado && ordenEncontrada.idCliente) {
      try {
        const clienteRes = await apiFetch(`/Cliente/${ordenEncontrada.idCliente}`);
        if (clienteRes) {
          docRegistrado = clienteRes.documento;
        }
      } catch (e) {
        // Ignorar
      }
    }

    return {
      success: true,
      data: {
        idRecepcionEquipo: ordenEncontrada.idRecepcionEquipo,
        numeroOrden: ordenEncontrada.numeroOrden,
        documentoRegistrado: docRegistrado,
        ordenBase: ordenEncontrada
      }
    };
  } catch (error) {
    console.error('Error buscando orden:', error);
    return {
      success: false,
      error: 'Error al comunicarse con el servidor. Intente nuevamente.'
    };
  }
};

/**
 * PASO 2: Valida la cédula ingresada contra la orden localizada.
 * Si coincide, entrega toda la información del equipo y la orden.
 * @param {object} ordenPrevia - Datos obtenidos en el Paso 1
 * @param {string} documentoIngresado - Cédula ingresada por el usuario
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const validarCedulaYObtenerOrden = async (ordenPrevia, documentoIngresado) => {
  try {
    const docLimpio = (documentoIngresado || '').trim();
    if (!docLimpio) {
      return { success: false, error: 'Por favor ingrese la cédula de identidad del titular.' };
    }

    const docIngresadoNormalizado = normalizarDocumento(docLimpio);

    // Caso Demo
    if (ordenPrevia.esDemo) {
      if (docIngresadoNormalizado === '12345678') {
        const tasaRes = await obtenerTasaActual();
        return {
          success: true,
          data: {
            ...ORDEN_DEMO,
            tasaCambio: tasaRes.precioUsdBs || 36.5
          }
        };
      } else {
        return {
          success: false,
          error: 'Por motivos de seguridad, la cédula no coincide con el titular de esta orden. Verifique el número e intente de nuevo.'
        };
      }
    }

    // Validación de seguridad estricta
    let documentoRegistrado = ordenPrevia.documentoRegistrado;

    // Si aún no tenemos el documento del cliente, cargarlo de la API
    if (!documentoRegistrado && ordenPrevia.ordenBase?.idCliente) {
      try {
        const clienteRes = await apiFetch(`/Cliente/${ordenPrevia.ordenBase.idCliente}`);
        if (clienteRes) {
          documentoRegistrado = clienteRes.documento;
        }
      } catch (cErr) {
        console.warn('Error obteniendo cliente:', cErr);
      }
    }

    const docRegistradoNormalizado = normalizarDocumento(documentoRegistrado);

    if (!docRegistradoNormalizado || docRegistradoNormalizado !== docIngresadoNormalizado) {
      return {
        success: false,
        error: 'Por seguridad, la cédula ingresada no coincide con el titular registrado en esta orden de servicio.'
      };
    }

    // Cédula coincide: cargar información completa con relaciones y tasa
    const id = ordenPrevia.idRecepcionEquipo;
    let relaciones = null;
    try {
      relaciones = await apiFetch(`/RecepcionEquipo/${id}/conRelaciones`);
    } catch (relErr) {
      console.warn('No se pudieron obtener relaciones detalladas:', relErr);
    }

    let clienteCompleto = null;
    if (ordenPrevia.ordenBase?.idCliente) {
      try {
        clienteCompleto = await apiFetch(`/Cliente/${ordenPrevia.ordenBase.idCliente}`);
      } catch (cErr) {
        // Ignorar
      }
    }

    let detalles = relaciones?.recepcionEquipoDetalles || relaciones?.detalles || ordenPrevia.ordenBase?.recepcionEquipoDetalles || [];
    if (!detalles || detalles.length === 0) {
      try {
        const detRes = await apiFetch(`/RecepcionEquipo/${id}/detalles`);
        if (Array.isArray(detRes) && detRes.length > 0) {
          detalles = detRes;
        }
      } catch (dErr) {
        // Ignorar
      }
    }

    const tasaInfo = await obtenerTasaActual();

    const ordenFinal = {
      ...(ordenPrevia.ordenBase || {}),
      ...(relaciones || {}),
      clienteFull: clienteCompleto,
      recepcionEquipoDetalles: detalles,
      tasaCambio: tasaInfo.precioUsdBs || 1,
      monedaSimbolo: tasaInfo.simbolo || 'Bs.'
    };

    return {
      success: true,
      data: ordenFinal
    };
  } catch (error) {
    console.error('Error validando cédula:', error);
    return {
      success: false,
      error: 'Error al verificar la información de seguridad. Intente de nuevo.'
    };
  }
};

/**
 * Obtiene la tasa de venta activa más reciente
 */
export const obtenerTasaActual = async () => {
  try {
    const res = await obtenerTasaVentaActual();
    if (res.success && res.data) {
      const tasa = res.data;
      return {
        precioUsdBs: tasa.precio_usd_bs || tasa.precioUsdBs || tasa.tasaUsdToBs || 1,
        simbolo: 'Bs.'
      };
    }
  } catch (e) {
    console.warn('No se pudo obtener tasa activa, usando valor por defecto:', e);
  }
  return { precioUsdBs: 1, simbolo: 'Bs.' };
};

export default {
  buscarOrdenPorNumero,
  validarCedulaYObtenerOrden,
  normalizarDocumento,
  obtenerTasaActual
};
